import { VIEW_REDACTED_IMAGE, UPDATED_USER } from '@app/constants/actions';
import { QUERY_KEYS } from '@app/constants/service';
import {
  updateRedactionViewedEvents,
  handleImageRedaction,
} from '@app/helpers/redaction';
import Message from '@app/models/message';
import User from '@app/models/user';

import findUpdatePageIndex from './cache-helpers';

const getChatMessagesQueryKey = (chatId: string) => [
  `${QUERY_KEYS.CHAT_MESSAGES}-${chatId}`,
];

type QueryClient = {
  setQueriesData: (
    arg0: string[],
    arg1: (oldData: { pages: unknown[] } | undefined) => unknown,
  ) => void;
};

type Store = {
  jobs: { activeChat: { id: string } };
  auth: { user: { _id: string; firstName: string; lastName: string } };
};

/**
 * Handles chat message updates in the cache.
 *
 * @param {Object} chat - The chat object containing id or _id.
 * @param {Message} message - The message object to be handled.
 * @param {string} eventName - The name of the event triggering the update.
 * @param {Object} queryClient - The query client for managing cache.
 * @param {Object} store - The Redux store containing jobs and auth state.
 * @param {User|undefined} updatedUserData - Updated user data if applicable.
 */
const handleChatMessageItem = (
  chat: { id: string; _id: string },
  message: Message,
  eventName: string,
  queryClient: QueryClient,
  store: Store,
  updatedUserData: User | undefined,
) => {
  const chatId =
    (typeof chat === 'string' ? chat : chat?.id || chat?._id) ||
    store?.jobs?.activeChat?.id;
  if (!chatId || !(message?._id || message?.id)) {
    return;
  }

  const updater = (oldData: { pages: unknown[] } | undefined) => {
    const allMessages = oldData?.pages?.flat() || [];
    const messageId = message?.id || message?._id;
    const shouldUpdate = allMessages.some(
      (msg) =>
        (msg as { id: string; _id: string })?.id === messageId ||
        (msg as { id: string; _id: string })?._id === messageId,
    );

    // when user data is updated
    if (updatedUserData && eventName === UPDATED_USER) {
      const pageIndex = findUpdatePageIndex(oldData, messageId);
      if (pageIndex === undefined || !oldData) {
        return oldData;
      }
      const updatedMessages = allMessages.map((msg) => {
        const typedMsg = msg as { author?: { id: string } };
        if (
          typedMsg?.author &&
          typedMsg?.author?.id &&
          typedMsg?.author?.id === updatedUserData?.id
        ) {
          return { ...typedMsg, author: updatedUserData };
        }
        return typedMsg;
      });
      const newData = { ...oldData };
      newData.pages[pageIndex] = updatedMessages;
      return newData;
    }

    // update message logic
    if (shouldUpdate) {
      const pageIndex = findUpdatePageIndex(oldData, messageId);
      if (pageIndex === undefined || !oldData) {
        return oldData;
      }
      const pageMessages = Array.isArray(oldData?.pages[pageIndex])
        ? oldData.pages[pageIndex]
        : [];
      const updatedMessages = Array.isArray(pageMessages)
        ? pageMessages.map(
            (msg: {
              id: string;
              _id: string;
              redactionViewedEvents: unknown[];
            }) => {
              if (msg.id === messageId || msg._id === messageId) {
                switch (eventName) {
                  case VIEW_REDACTED_IMAGE: {
                    return {
                      ...msg,
                      redactionViewedEvents: updateRedactionViewedEvents(
                        msg?.redactionViewedEvents,
                        store?.auth?.user,
                      ),
                    };
                  }
                  default: {
                    const updatedMessage = { ...msg, ...message };
                    return handleImageRedaction(
                      updatedMessage,
                      updatedMessage?.redactionData,
                    );
                  }
                }
              }
              return msg;
            },
          )
        : [];
      const newData = {
        ...oldData,
        pages: oldData.pages.map((page, index) =>
          index === pageIndex ? updatedMessages : page,
        ),
      };
      return newData;
    }
    // add message logic — handle oldData being undefined (e.g. realtime event
    // arrives while the initial fetch is in flight, or the cache entry was
    // evicted) by seeding with the new message so the updater never crashes.
    const existingPages = oldData?.pages ?? [];
    const existingFirstPage = (existingPages[0] as unknown[]) || [];
    const updatedFirstPage = [
      { ...message, isNewMessage: true },
      ...existingFirstPage,
    ];
    const updatedPages =
      existingPages.length > 0
        ? [updatedFirstPage, ...existingPages.slice(1)]
        : [updatedFirstPage];
    return {
      ...(oldData ?? { pageParams: [undefined] }),
      pages: updatedPages,
    };
  };

  queryClient.setQueriesData(getChatMessagesQueryKey(chatId), updater);
};

/**
 * Handles dismissing needs action for chat messages in the cache.
 *
 * @param {Object} chat - The chat object containing id or _id.
 * @param {Object} queryClient - The query client for managing cache.
 * @param {Object} store - The Redux store containing jobs and auth state.
 */
export const handleDismissNeedsAction = (
  chat: { id: string; _id: string },
  queryClient: QueryClient,
  store: Store,
) => {
  const chatId =
    (typeof chat === 'string' ? chat : chat?.id || chat?._id) ||
    store?.jobs?.activeChat?.id;

  if (!chatId) {
    return;
  }

  const updater = (oldData: { pages: unknown[] } | undefined) => {
    if (!oldData || !Array.isArray(oldData?.pages)) {
      return oldData;
    }
    return {
      ...oldData,
      pages: oldData?.pages?.map((page) => {
        if (!Array.isArray(page)) {
          return page;
        }
        return page.map((msg: Message | null | undefined) => {
          if (!msg) {
            return msg;
          }
          // Only update needsAction if it is not already an empty object
          if (
            !msg.needsAction ||
            (msg.needsAction &&
              typeof msg.needsAction === 'object' &&
              !Array.isArray(msg.needsAction) &&
              Object.keys(msg.needsAction).length > 0)
          ) {
            return { ...msg, needsAction: {} };
          }
          return msg;
        });
      }),
    };
  };
  queryClient.setQueriesData(getChatMessagesQueryKey(chatId), updater);
};

export const getChatId = (chat: { id: string; _id: string }, store: Store) => {
  if (typeof chat === 'object') {
    return chat?.id || chat?._id || store?.jobs?.activeChat?.id;
  }
  return chat;
};

// update old message in the cache as 'retryAttempted' to update styling
export const handleChatMessageRetry = (
  chat: { id: string; _id: string },
  messageId: string,
  queryClient: QueryClient,
  store: Store,
) => {
  const chatId = getChatId(chat, store);

  if (!chatId) {
    return;
  }

  const updater = (oldData: { pages: unknown[] } | undefined) => {
    if (!oldData || !Array.isArray(oldData?.pages)) {
      return oldData;
    }
    return {
      ...oldData,
      pages: oldData?.pages?.map((page) => {
        if (!Array.isArray(page)) {
          return page;
        }
        return page.map((msg: Message | null | undefined) => {
          if (msg?.id === messageId) {
            return { ...msg, retryAttempted: true };
          }
          return msg;
        });
      }),
    };
  };
  queryClient.setQueriesData(getChatMessagesQueryKey(chatId), updater);
};

export const handleDismissAllNeedsAttention = (
  chat: { id: string; _id: string },
  queryClient: QueryClient,
  store: Store,
) => {
  const chatId = getChatId(chat, store);

  if (!chatId) {
    return;
  }

  const updater = (oldData: { pages: unknown[] } | undefined) => {
    if (!oldData || !Array.isArray(oldData?.pages)) {
      return oldData;
    }
    return {
      ...oldData,
      pages: oldData?.pages?.map((page) => {
        if (!Array.isArray(page)) {
          return page;
        }
        return page.map((msg: Message | null | undefined) => {
          if (msg?.needsAttention) {
            return {
              ...msg,
              needsAttention: false,
              hasDismissedNeedsAttention: true,
            };
          }
          return msg;
        });
      }),
    };
  };
  queryClient.setQueriesData(getChatMessagesQueryKey(chatId), updater);
};

export default handleChatMessageItem;
